import React, { useEffect, useState } from "react";
import { Wallet, AlertTriangle, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { useI18n } from "../i18n/LanguageContext";
import { useAuth } from "../context/AuthContext";
import { useConfig, symbolForCurrency, nf, formatMoney } from "../lib/format";
import { api } from "../lib/api";

const BAND_COLOR = { ok: "#D4AF37", warn: "#F59E0B", over: "#C8102E" };

// Per-category budget card (READ-ONLY). The reserved amount is a consequence of
// what the user defined at the start ("Meu Orçamento"). To change it, the user
// edits each category there — not here.
export function CategoryBudget({ category, aside, savedSum }) {
  const { t } = useI18n();
  const { user } = useAuth();
  const config = useConfig();
  const symbol = symbolForCurrency(config, user?.currency || "BRL");
  const fmt = (v) => formatMoney(symbol, v || 0);
  const [cat, setCat] = useState(null);
  const [spentDraft, setSpentDraft] = useState("");
  const [savingSpent, setSavingSpent] = useState(false);
  const manualSpend = category === "ceia";

  useEffect(() => {
    api.get("/budget").then(({ data }) => { const c = data.budget?.categories?.[category] || {}; setCat(c); if (c.spent != null) setSpentDraft(String(c.spent)); }).catch(() => {});
  }, [category]);

  const saveSpent = async (amount) => {
    setSavingSpent(true);
    try {
      const { data } = await api.put("/budget/spent", { category, amount });
      const c = data.budget?.categories?.[category] || {};
      setCat(c); setSpentDraft(c.spent != null ? String(c.spent) : "");
      toast.success(t("cbud.spent_saved"));
    } catch { toast.error("Erro"); }
    setSavingSpent(false);
  };
  const submitSpent = (e) => { e.preventDefault(); const v = Number(String(spentDraft).replace(",", ".")); if (Number.isFinite(v) && v >= 0) saveSpent(v); };

  const raw = cat || {};
  // Planned = total of "Meus salvamentos"; Available = limit - planned.
  const planned = savedSum != null ? Number(savedSum) : Number(raw.planned || 0);
  const c = raw.limit != null
    ? { ...raw, planned, available: Math.round((Number(raw.limit) - planned) * 100) / 100, over: Math.max(0, Math.round((planned - Number(raw.limit)) * 100) / 100) }
    : { ...raw, planned };
  const pct = c.limit ? Math.min(100, Math.round((planned / c.limit) * 100)) : 0;
  const band = c.over > 0 ? "over" : (c.limit && planned / c.limit >= 0.9 ? "warn" : c.band);

  return (
    <div className="glass mb-8 rounded-2xl p-6" data-testid={`catbudget-${category}`}>
      <div className="mb-1 flex items-center gap-2"><Wallet className="h-5 w-5 text-xmas-gold" /><h2 className="font-display text-lg text-xmas-snow">{t("cbud.title")}</h2></div>
      <p className="mb-4 text-sm text-xmas-slate">{t("cbud.sub")}</p>
      <div className={aside ? "grid grid-cols-1 gap-6 md:grid-cols-2" : ""}>
      <div>
      <div className="flex flex-wrap items-center gap-3">
        <span className="rounded-xl border border-xmas-gold/20 bg-xmas-navy/50 px-5 py-3 font-mono-num text-2xl font-bold text-xmas-gold" data-testid={`catbudget-value-${category}`}>{fmt(c.limit)}</span>
        {savedSum != null && c.limit != null && (
          <div className="flex flex-col text-xs" data-testid={`catbudget-saves-${category}`}>
            <span className="text-xmas-slate">{t("saves.title")}: <b className="font-mono-num text-xmas-snow" data-testid={`catbudget-saves-sum-${category}`}>{fmt(savedSum)}</b></span>
            <span className={c.over > 0 ? "text-xmas-crimson" : "text-xmas-champagne"}>{t("budget.available2")}: <b className="font-mono-num" data-testid={`catbudget-saves-avail-${category}`}>{fmt(c.available)}</b>{c.over > 0 && <AlertTriangle className="ml-1 inline h-3 w-3" />}</span>
          </div>
        )}
      </div>
      {manualSpend && (
        <div className="mt-4" data-testid={`catbudget-spent-${category}`}>
          <p className="text-xs text-xmas-slate">{t("cbud.spent_label")}</p>
          <form onSubmit={submitSpent} className="mt-2 flex flex-wrap items-center gap-2">
            <span className="font-mono-num text-sm text-xmas-champagne">{symbol}</span>
            <input type="number" inputMode="decimal" min="0" step="0.01" value={spentDraft} onChange={(e) => setSpentDraft(e.target.value)} placeholder={t("cbud.spent_ph")} data-testid={`catbudget-spent-input-${category}`}
              className="w-36 rounded-full border border-xmas-gold/25 bg-xmas-navy/60 px-4 py-2 font-mono-num text-sm text-xmas-snow outline-none focus:border-xmas-gold" />
            <button type="submit" disabled={savingSpent || spentDraft === ""} data-testid={`catbudget-spent-save-${category}`} className="btn-gold rounded-full px-4 py-2 text-xs font-bold uppercase tracking-wider disabled:opacity-50">{t("cbud.spent_save")}</button>
            {c.spent != null && <button type="button" onClick={() => saveSpent(null)} disabled={savingSpent} data-testid={`catbudget-spent-clear-${category}`} className="text-xs text-xmas-slate underline-offset-4 hover:text-xmas-snow hover:underline">{t("cbud.spent_clear")}</button>}
          </form>
          {c.spent != null && c.limit != null && (
            <div className="mt-3 text-sm" data-testid={`catbudget-spent-result-${category}`}>
              <p className="text-xs text-xmas-slate">{t("cbud.spent_of", { s: fmt(c.spent), l: fmt(c.limit) })}</p>
              {c.over > 0
                ? <p className="mt-1 inline-flex items-center gap-1.5 font-semibold text-xmas-crimson"><AlertTriangle className="h-4 w-4" /> {t("cbud.spent_over", { v: fmt(c.over) })}</p>
                : <p className="mt-1 inline-flex items-center gap-1.5 font-semibold text-emerald-400"><CheckCircle2 className="h-4 w-4" /> {t("cbud.spent_left", { v: fmt(c.available) })}</p>}
              <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-xmas-navysurface">
                <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, background: c.over > 0 ? BAND_COLOR.over : BAND_COLOR.ok }} />
              </div>
            </div>
          )}
        </div>
      )}
      {!manualSpend && <>
      <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-xs">
        <span className="text-xmas-slate">{c.limit != null ? t("budget.of", { p: fmt(c.planned), l: fmt(c.limit) }) : `${t("budget.planned_amt")}: ${fmt(c.planned || 0)}`}</span>
        {c.available != null && (
          <span className={c.over > 0 ? "text-xmas-crimson" : "text-xmas-champagne"}>
            {c.over > 0 ? t("budget.band_over", { v: fmt(c.over) }) : `${fmt(c.available)} ${t("budget.available2").toLowerCase()}`}
          </span>
        )}
      </div>
      {c.limit ? (
        <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-xmas-navysurface">
          <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, background: BAND_COLOR[band] || "#D4AF37" }} />
        </div>
      ) : null}
      </>}
      </div>
      {aside && (
        <div className="border-t border-xmas-gold/15 pt-4 md:border-l md:border-t-0 md:pl-6 md:pt-0">{aside}</div>
      )}
      </div>
    </div>
  );
}
